/**
 * Xuất báo cáo phía client (Giai đoạn 4 — mục "Xuất báo cáo PDF/Excel").
 *
 * Client-side để không đụng backend/framework: Excel bằng `xlsx` (Unicode sẵn),
 * PDF bằng `jspdf` + `jspdf-autotable` với font Roboto nhúng (font mặc định của jsPDF
 * VỠ DẤU tiếng Việt). Tất cả thư viện + font đều **lazy-import** trong hàm nên KHÔNG
 * nằm trong main bundle — chỉ tải khi người dùng bấm xuất.
 *
 * Gate theo gói (can_export_pdf/can_export_excel) làm ở tầng UI (ExportButtons) — hàm
 * này thuần kỹ thuật, không kiểm quyền.
 */

/** Chuẩn hoá tên file: bỏ ký tự lạ, cắt gọn, thêm đuôi. */
function safeName(name, ext) {
	const base = String(name || 'bao-cao')
		.normalize('NFKD').replace(/[̀-ͯ]/g, '') // bỏ dấu kết hợp cho tên file
		.replace(/đ/g, 'd').replace(/Đ/g, 'D')
		.replace(/[^a-zA-Z0-9-_]+/g, '-')
		.replace(/-+/g, '-').replace(/^-|-$/g, '')
		.slice(0, 80) || 'bao-cao';
	return base.endsWith('.' + ext) ? base : `${base}.${ext}`;
}

/**
 * Xuất Excel nhiều sheet.
 * @param {string} filename tên file (không cần đuôi)
 * @param {Array<{name:string, aoa:any[][]}>} sheets mỗi sheet là mảng-hàng (array of arrays)
 */
export async function exportExcel(filename, sheets) {
	const XLSX = await import('xlsx');
	const wb = XLSX.utils.book_new();

	(sheets || []).forEach((s, i) => {
		const ws = XLSX.utils.aoa_to_sheet(s.aoa || []);
		// Tên sheet Excel tối đa 31 ký tự, không chứa : \ / ? * [ ].
		const name = String(s.name || `Sheet${i + 1}`).replace(/[:\\/?*[\]]/g, ' ').slice(0, 31) || `Sheet${i + 1}`;
		XLSX.utils.book_append_sheet(wb, ws, name);
	});

	XLSX.writeFile(wb, safeName(filename, 'xlsx'));
}

/**
 * Xuất PDF gồm tiêu đề + nhiều bảng (mỗi section 1 bảng autoTable).
 * @param {string} filename
 * @param {{title:string, subtitle?:string, sections:Array<{heading?:string, columns:string[], rows:any[][]}>}} doc
 */
export async function exportPdf(filename, {title, subtitle, sections} = {}) {
	const {jsPDF} = await import('jspdf');
	const autoTable = (await import('jspdf-autotable')).default;
	const {robotoRegular} = await import('./fonts/robotoBase64');

	const doc = new jsPDF({unit: 'pt', format: 'a4'});

	// Nhúng font Roboto để render tiếng Việt (font core của jsPDF không hỗ trợ).
	doc.addFileToVFS('Roboto-Regular.ttf', robotoRegular);
	doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
	doc.setFont('Roboto', 'normal');

	const marginX = 40;
	let y = 48;

	doc.setFontSize(16);
	doc.text(String(title || 'Báo cáo'), marginX, y);
	y += 18;

	if (subtitle) {
		doc.setFontSize(10);
		doc.setTextColor(120);
		doc.text(String(subtitle), marginX, y);
		doc.setTextColor(0);
		y += 12;
	}

	(sections || []).forEach((sec) => {
		if (sec.heading) {
			doc.setFontSize(12);
			doc.setTextColor(220, 38, 38);
			doc.text(String(sec.heading), marginX, y + 28);
			doc.setTextColor(0);
			y += 34;
		}

		autoTable(doc, {
			startY: y + 6,
			head: [sec.columns || []],
			body: sec.rows || [],
			styles: {font: 'Roboto', fontSize: 9, cellPadding: 4, overflow: 'linebreak'},
			headStyles: {font: 'Roboto', fillColor: [243, 244, 246], textColor: 30},
			margin: {left: marginX, right: marginX},
		});

		y = doc.lastAutoTable.finalY;
	});

	doc.save(safeName(filename, 'pdf'));
}
